import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@mui/material'
import Tabs from '@/shared/components/Tabs/Tabs'
import Chip from '@/shared/components/Chip/Chip'
import { currencySymbol } from '@/shared/utils/price'
import { EmployeeMetrics } from '../../hooks/useEmployeeMetrics'
import styles from './EmployeeRanking.module.scss'

export interface RankableEmployee {
  id: string
  name: string
  metrics: EmployeeMetrics
}

type RankingMetric = 'rating' | 'earners' | 'hours' | 'attendance'

interface EmployeeRankingProps {
  employees: RankableEmployee[]
  topN?: number
}

/**
 * Direction of a place change.
 *
 * Standing still is a dash, not a horizontal arrow: an arrow of any kind reads
 * as movement, and sideways movement is not a thing a ranking has. The chip
 * still carries a number, so all three keep the same shape and width.
 */
function deltaIcon(delta: number): string {
  if (delta > 0) return 'la-arrow-up'
  if (delta < 0) return 'la-arrow-down'
  return 'la-minus'
}

function formatValue(metric: RankingMetric, value: number): string {
  switch (metric) {
    case 'rating':
      return value.toFixed(1)
    case 'earners':
      return `${value.toFixed(0)} ${currencySymbol()}`
    case 'hours':
      return `${value}h`
    case 'attendance':
      return `${value}%`
    default:
      return String(value)
  }
}

function metricValue(metric: RankingMetric, m: EmployeeMetrics): number {
  switch (metric) {
    case 'rating':
      return m.serviceRating
    case 'earners':
      return m.revenue
    case 'hours':
      return m.hoursThisWeek
    case 'attendance':
      return m.attendancePct
    default:
      return 0
  }
}

function previousMetricValue(metric: RankingMetric, m: EmployeeMetrics): number {
  switch (metric) {
    case 'rating':
      return m.previousServiceRating
    case 'earners':
      return m.previousRevenue
    case 'hours':
      return m.previousHoursThisWeek
    case 'attendance':
      return m.previousAttendancePct
    default:
      return 0
  }
}

function initialsOf(name: string | undefined | null): string {
  if (!name) return ''
  return name
    .split(' ')
    .filter(Boolean)
    .map(part => part[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('')
}

function EmployeeRanking(props: EmployeeRankingProps) {
  const { employees, topN = 5 } = props
  const { t } = useTranslation()
  const [metric, setMetric] = useState<RankingMetric>('rating')

  const tabs = useMemo(
    () => [
      {
        label: t('EMPLOYEES.RANKING.TAB.RATING'),
        value: 'rating' as RankingMetric
      },
      {
        label: t('EMPLOYEES.RANKING.TAB.EARNERS'),
        value: 'earners' as RankingMetric
      },
      {
        label: t('EMPLOYEES.RANKING.TAB.HOURS'),
        value: 'hours' as RankingMetric
      },
      {
        label: t('EMPLOYEES.RANKING.TAB.ATTENDANCE'),
        value: 'attendance' as RankingMetric
      }
    ],
    [t]
  )

  const previousRankById = useMemo(() => {
    const map = new Map<string, number>()
    // Only people who registered on the metric last period get a previous
    // place. Ranking a field of zeros would hand out positions by array order
    // and every trend chip after it would be noise.
    const sorted = employees
      .filter(e => previousMetricValue(metric, e.metrics) > 0)
      .sort(
        (a, b) =>
          previousMetricValue(metric, b.metrics) -
          previousMetricValue(metric, a.metrics)
      )
    sorted.forEach((e, idx) => map.set(e.id, idx + 1))
    return map
  }, [employees, metric])

  const ranked = useMemo(() => {
    const scored = employees
      .map(e => ({
        ...e,
        score: metricValue(metric, e.metrics)
      }))
      // A zero is not a placing: with nothing rostered every hours score is 0,
      // and a podium of empty bars reads as a result rather than as no data.
      .filter(e => e.score > 0)
    scored.sort((a, b) => b.score - a.score)
    return scored.slice(0, topN)
  }, [employees, metric, topN])

  const topScore = ranked[0]?.score ?? 0

  const description = t(`EMPLOYEES.RANKING.DESCRIPTION.${metric.toUpperCase()}`)

  return (
    <div className={styles.wrapper}>
      <p className={`title-bold-medium ${styles.title}`}>
        {t('EMPLOYEES.RANKING.TITLE')}
      </p>
      <p className={`body-small ${styles.description}`}>{description}</p>

      <div className={styles.tabsRow}>
        <Tabs
          tabs={tabs}
          value={metric}
          onChange={value => setMetric(value as RankingMetric)}
        />
      </div>
      <div className={styles.list}>
        {ranked.length === 0 && (
          <p className={`body-medium ${styles.emptyState}`}>
            {t(
              employees.length
                ? 'EMPLOYEES.RANKING.NO_DATA'
                : 'EMPLOYEES.RANKING.EMPTY'
            )}
          </p>
        )}
        {ranked.map((employee, idx) => {
          const widthPct =
            topScore > 0 ? Math.max(8, (employee.score / topScore) * 100) : 0
          const currentRank = idx + 1
          const previousRank = previousRankById.get(employee.id)
          const delta =
            previousRank !== undefined ? previousRank - currentRank : 0
          return (
            <div key={employee.id} className={styles.row}>
              <span className={`title-bold-medium ${styles.rank}`}>
                {currentRank}
              </span>
              <Avatar
                alt={employee.name ?? ''}
                sx={{ width: '2rem', height: '2rem' }}
              >
                <p className="body-bold-small">{initialsOf(employee.name)}</p>
              </Avatar>
              <div className={styles.rowBody}>
                <div className={styles.rowHeader}>
                  <p className={`body-bold-medium ${styles.name}`}>
                    {employee.name ?? '—'}
                  </p>
                  <p className={`body-bold-medium ${styles.value}`}>
                    {formatValue(metric, employee.score)}
                  </p>
                </div>
                <div className={styles.barTrack}>
                  <div
                    className={styles.barFill}
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
              </div>
              {/*
                Always rendered, standing still included. Leaving the cell out
                let `rowBody` claim the space instead, so a row that had not
                moved got a LONGER bar track than the rows around it, and the
                bars are the comparison, so the same score drew a different
                length depending on last week. The fixed width keeps every
                track identical whatever the chip says.
              */}
              <div className={styles.delta}>
                <Chip
                  label={String(Math.abs(delta))}
                  color={delta < 0 ? 'secondary' : 'primary'}
                  chipIcon={<i className={`las ${deltaIcon(delta)}`} />}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default EmployeeRanking
